<template>
  <div class="pa-6">
    <div class="list-header mb-5">
      <div class="body-2 grey--text text--darken-1">
        The supplier isn't offered on quotes for these dates. Dates also come from availability requests the
        supplier (or staff) answered <strong>Not Available</strong>.
      </div>
      <div class="d-flex align-center" style="gap:12px;">
        <v-switch v-model="include_past" dense hide-details class="mt-0" label="Show past" @change="load()" />
        <v-btn color="#525A68" rounded dark elevation="0" class="create-btn" @click="openForm()">
          <v-icon left small>mdi-plus</v-icon>
          Add Dates
        </v-btn>
      </div>
    </div>

    <v-data-table :headers="headers" :items="rows" :loading="loading" dense disable-pagination hide-default-footer
      no-data-text="No unavailable dates">
      <template v-slot:item.dates="{ item }">
        <strong>{{ fmt(item.start_date) }}</strong><span v-if="item.end_date !== item.start_date"> – <strong>{{ fmt(item.end_date) }}</strong></span>
        <div class="meta">{{ days(item) }} day{{ days(item) === 1 ? '' : 's' }}</div>
      </template>
      <template v-slot:item.reason="{ item }">
        <div>{{ item.reason || '—' }}</div>
        <div v-if="item.notes" class="meta" style="white-space:pre-line;">{{ item.notes }}</div>
      </template>
      <template v-slot:item.source="{ item }">
        <span class="source-chip" :class="item.source === 'supplier' ? 'source-chip--supplier' : ''">
          {{ item.source === 'supplier' ? 'Supplier (link)' : (item.creator ? item.creator.name : 'Staff') }}
        </span>
        <div v-if="item.availability_request" class="meta">
          <router-link :to="{ name: 'EditLeads', params: { id: item.availability_request.lead_id } }">From an availability request</router-link>
        </div>
      </template>
      <template v-slot:item.action="{ item }">
        <v-btn icon small :disabled="!!item.availability_request_id" title="Change the answer on the lead's availability request instead"
          @click="openForm(item)"><v-icon small>mdi-pencil-outline</v-icon></v-btn>
        <v-btn icon small @click="remove(item)"><v-icon small color="red">mdi-delete-outline</v-icon></v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="form_dialog" max-width="480px" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">{{ form.id ? 'Edit' : 'Add' }} Unavailable Dates</v-card-title>
        <v-card-text>
          <v-form ref="form">
            <v-row dense>
              <v-col cols="6">
                <v-text-field v-model="form.start_date" type="date" label="From" outlined dense
                  :rules="[v => !!v || 'Required']" />
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="form.end_date" type="date" label="To (inclusive)" outlined dense :min="form.start_date"
                  :rules="[v => !!v || 'Required', v => !form.start_date || v >= form.start_date || 'Must be on or after From']" />
              </v-col>
            </v-row>
            <v-text-field v-model="form.reason" label="Reason" outlined dense placeholder="e.g. Fully booked, closed for refurb" />
            <v-textarea v-model="form.notes" label="Notes" outlined dense rows="3" hide-details />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="form_dialog = false">Cancel</v-btn>
          <v-btn color="#3988AA" dark :loading="saving" @click="save()">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Supplier setup → Unavailable Dates (docs/reference/SUPPLIER_UNAVAILABILITY.md).
export default {
  props: {
    supplierId: { type: Number, required: true },
  },
  data: () => ({
    rows: [],
    loading: false,
    saving: false,
    include_past: false,
    form_dialog: false,
    form: {},
    headers: [
      { text: 'Dates', value: 'dates', sortable: false, width: '220px' },
      { text: 'Reason', value: 'reason', sortable: false },
      { text: 'Added by', value: 'source', sortable: false, width: '220px' },
      { text: '', value: 'action', sortable: false, width: '90px', align: 'end' },
    ],
  }),
  mounted() {
    this.load()
  },
  methods: {
    fmt(value) {
      return this.$date(value).format('ddd DD/MM/YYYY')
    },
    days(item) {
      return this.$date(item.end_date).diff(this.$date(item.start_date), 'day') + 1
    },
    async load() {
      this.loading = true
      try {
        const { data } = await this.$axios.get(`admin/suppliers/get_unavailability/${this.supplierId}`, {
          params: this.include_past ? { include_past: 1 } : {},
        })
        this.rows = data.data || []
      } finally {
        this.loading = false
      }
    },
    openForm(item = null) {
      const today = this.$date().format('YYYY-MM-DD')
      this.form = item
        ? { id: item.id, start_date: item.start_date, end_date: item.end_date, reason: item.reason, notes: item.notes }
        : { id: null, start_date: today, end_date: today, reason: '', notes: '' }
      this.form_dialog = true
    },
    async save() {
      if (!this.$refs.form.validate()) return
      this.saving = true
      try {
        const payload = { supplier_id: this.supplierId, ...this.form }
        const { data } = this.form.id
          ? await this.$axios.post(`admin/suppliers/update_unavailability/${this.form.id}`, payload)
          : await this.$axios.post('admin/suppliers/create_unavailability', payload)
        this.form_dialog = false
        this.$toast.success(data.message)
        await this.load()
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Failed to save')
      } finally {
        this.saving = false
      }
    },
    async remove(item) {
      const extra = item.availability_request_id ? ' The availability request stays as answered.' : ''
      if (!confirm(`Remove these unavailable dates?${extra}`)) return
      try {
        await this.$axios.delete(`admin/suppliers/delete_unavailability/${item.id}`)
        await this.load()
      } catch (e) {
        this.$toast.error(e.response?.data?.message || 'Failed to remove')
      }
    },
  },
}
</script>

<style scoped lang="scss">
.list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.create-btn {
  text-transform: none !important;
}

.meta {
  font-size: 12px;
  color: #7C7C7C;
}

.source-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  background: #EAF4F8;
  color: #2a6a88;

  &--supplier {
    background: #FFEBEE;
    color: #C62828;
  }
}
</style>
